import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import BidTiersEditor from "@/components/BidTiersEditor";
import ImageInput from "@/components/ImageInput";
import RegistrationFormConfigEditor from "@/pages/manage/RegistrationFormConfigEditor";
import CategoryConfigEditor from "@/pages/manage/CategoryConfigEditor";

const STATUSES = ["upcoming", "live", "completed", "cancelled"];

export default function SettingsTab({ auction, reload }) {
  const { user } = useAuth();
  const isSuperadmin = user?.role === "superadmin";
  const [form, setForm] = useState({
    name: auction.name || "",
    description: auction.description || "",
    registration_description: auction.registration_description || "",
    venue: auction.venue || "",
    banner_url: auction.banner_url || "",
    sponsor_text: auction.sponsor_text || "",
    start_date: (auction.start_date || "").slice(0, 10),
    end_date: (auction.end_date || "").slice(0, 10),
    status: auction.status || "upcoming",
    number_of_teams: auction.number_of_teams ?? Math.max(2, auction.team_count || 2),
    sold_media_url: auction.settings?.sold_media_url ?? "",
    unsold_media_url: auction.settings?.unsold_media_url ?? "",
    announce_duration_ms: auction.settings?.announce_duration_ms ?? 4000,
    default_team_budget: auction.settings?.default_team_budget ?? 1000,
    default_base_price: auction.settings?.default_base_price ?? 10,
    bid_increments: auction.settings?.bid_increments ?? [{ up_to: null, step: 10 }],
    max_players_per_team: auction.settings?.max_players_per_team ?? 15,
    min_players_per_team: auction.settings?.min_players_per_team ?? 11,
    timer_seconds: auction.settings?.timer_seconds ?? 30,
    accent_color: auction.accent_color || "",
    custom_led_logo_url: auction.custom_led_logo_url || "",
    // Iter33: registration payment gate
    registration_fee: auction.registration_fee ?? 0,
    payment_qr_url: auction.payment_qr_url || "",
    // Iter36: superadmin-set expected payee name — screenshot's payee must match
    expected_payee_name: auction.expected_payee_name || "",
    // Iter24: per-category config + icon auction toggle
    category_config: auction.settings?.category_config || {},
    icon_auction_enabled: auction.settings?.icon_auction_enabled !== false,
    allow_custom_led_logo: !!auction.allow_custom_led_logo,
    // Iter39: superadmin-selected LED overlay theme
    led_theme: auction.led_theme || "classic",
  });
  const [busy, setBusy] = useState(false);
  const canCustomLogo = form.allow_custom_led_logo;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const patch = {
        name: form.name,
        description: form.description || null,
        registration_description: form.registration_description || null,
        venue: form.venue || null,
        banner_url: form.banner_url || null,
        sponsor_text: form.sponsor_text || null,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        status: form.status,
        accent_color: form.accent_color || null,
        registration_fee: Number(form.registration_fee) || 0,
        payment_qr_url: form.payment_qr_url || null,
        expected_payee_name: form.expected_payee_name?.trim() || null,
        settings: {
          default_team_budget: Number(form.default_team_budget) || 1000,
          default_base_price: Number(form.default_base_price) || 10,
          bid_increments: form.bid_increments,
          max_players_per_team: Number(form.max_players_per_team) || 15,
          min_players_per_team: Number(form.min_players_per_team) || 11,
          timer_seconds: Number(form.timer_seconds) || 30,
          sold_media_url: form.sold_media_url || null,
          unsold_media_url: form.unsold_media_url || null,
          announce_duration_ms: Number(form.announce_duration_ms) || 4000,
          category_config: form.category_config || {},
          icon_auction_enabled: !!form.icon_auction_enabled,
        },
      };
      if (canCustomLogo) patch.custom_led_logo_url = form.custom_led_logo_url || null;
      // Superadmin-only fields — sending as regular admin is silently ignored by backend.
      if (isSuperadmin) {
        patch.allow_custom_led_logo = !!form.allow_custom_led_logo;
        patch.led_theme = form.led_theme === "royal_gold" ? "royal_gold" : "classic";
        const n = Number(form.number_of_teams);
        if (!Number.isNaN(n) && n !== auction.number_of_teams) {
          if (n < (auction.team_count || 0)) {
            toast.error(`Cannot reduce below existing ${auction.team_count} team(s).`);
            setBusy(false);
            return;
          }
          patch.number_of_teams = n;
        }
      }
      await api.patch(`/auctions/${auction.id}`, patch);
      toast.success("Auction updated");
      reload?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const set = (k, v) => setForm({ ...form, [k]: v });

  return (
    <form onSubmit={save} className="space-y-6 max-w-3xl" data-testid="settings-tab">
      <div className="label-mono">AUCTION SETTINGS</div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className="label-mono block mb-2">Name*</label>
          <input required value={form.name} onChange={(e) => set("name", e.target.value)} className="w-full bg-white border border-gray-200 rounded px-3 py-2 focus:border-blue-500 outline-none" />
        </div>
        <div className="sm:col-span-2">
          <label className="label-mono block mb-2">Description</label>
          <textarea rows="2" value={form.description} onChange={(e) => set("description", e.target.value)} className="w-full bg-white border border-gray-200 rounded px-3 py-2 focus:border-blue-500 outline-none" />
        </div>
        <div className="sm:col-span-2">
          <label className="label-mono block mb-2">Registration Page Description · long text shown on the public /register form</label>
          <textarea
            rows="6"
            value={form.registration_description}
            onChange={(e) => set("registration_description", e.target.value)}
            placeholder={"Paste tournament details, eligibility, prize pool… shown next to the registration form."}
            data-testid="settings-registration-description"
            className="w-full bg-white border border-gray-200 rounded px-3 py-2 focus:border-blue-500 outline-none whitespace-pre-wrap"
          />
          <div className="text-[10px] text-gray-500 mt-1">Line breaks are preserved. Use to explain rules, age criteria, sponsors, etc.</div>
        </div>
        <div className="sm:col-span-2">
          <ImageInput
            label="Tournament Banner (16:9 recommended)"
            value={form.banner_url}
            onChange={(v) => set("banner_url", v)}
            testId="settings-banner-input"
            maxKB={5120}
            aspect="aspect-video"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="label-mono block mb-2">Sponsor slot text · shown on LED overlay</label>
          <input
            value={form.sponsor_text}
            onChange={(e) => set("sponsor_text", e.target.value)}
            placeholder={"Powered by NitiSoft"}
            data-testid="settings-sponsor-text"
            className="w-full bg-white border border-gray-200 rounded px-3 py-2 focus:border-amber-500 outline-none"
          />
          <div className="text-[10px] text-gray-500 mt-1">Sell this slot to a local sponsor — one small tagline appears on the LED broadcast for the whole tournament.</div>
        </div>
        <div>
          <label className="label-mono block mb-2">Accent Color · applied to overlays</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={form.accent_color || "#F97316"}
              onChange={(e) => set("accent_color", e.target.value)}
              className="w-14 h-10 rounded border border-gray-200 cursor-pointer"
              data-testid="settings-accent-color-picker"
            />
            <input
              type="text"
              value={form.accent_color}
              onChange={(e) => set("accent_color", e.target.value)}
              placeholder="#F97316 (default)"
              className="flex-1 bg-white border border-gray-200 rounded px-3 py-2 focus:border-amber-500 outline-none font-mono uppercase text-sm"
              data-testid="settings-accent-color-hex"
            />
            {form.accent_color && (
              <button type="button" onClick={() => set("accent_color", "")} data-testid="settings-accent-color-clear" className="text-xs text-gray-500 hover:text-red-500">✕ Reset</button>
            )}
          </div>
          <div className="text-[10px] text-gray-500 mt-1">Leave empty to use the default orange/red theme.</div>
        </div>
        <div>
          <label className="label-mono block mb-2">
            Custom LED Logo <span className="normal-case text-gray-400 tracking-normal font-normal">— replaces NitiSoft mark on LED broadcast</span>
          </label>
          {isSuperadmin && (
            <label className="flex items-center gap-2 text-xs mb-2 cursor-pointer" data-testid="settings-allow-custom-led-logo-toggle">
              <input
                type="checkbox"
                checked={!!form.allow_custom_led_logo}
                onChange={(e) => set("allow_custom_led_logo", e.target.checked)}
                className="w-4 h-4 accent-orange-500"
              />
              <span className="font-bold uppercase tracking-widest text-orange-600">Allow custom LED logo (Superadmin)</span>
            </label>
          )}
          {canCustomLogo ? (
            <div>
              <ImageInput
                value={form.custom_led_logo_url}
                onChange={(v) => set("custom_led_logo_url", v)}
                testId="settings-custom-led-logo"
                maxKB={2048}
                aspect="aspect-square"
              />
              <div className="text-[10px] text-gray-500 mt-1">PNG with transparent background recommended.</div>
            </div>
          ) : (
            <div className="text-[11px] rounded border border-dashed border-gray-300 px-3 py-2 text-gray-500 bg-gray-50">
              {isSuperadmin
                ? "Enable the toggle above to upload a custom LED logo."
                : "🔒 Custom LED logo is disabled. Ask the superadmin to enable it for this auction."}
            </div>
          )}
        </div>
        {isSuperadmin && (
          <div className="md:col-span-2 border-t border-gray-200 pt-4">
            <label className="label-mono block mb-2">
              LED Overlay Theme <span className="normal-case text-gray-400 tracking-normal font-normal">— pick the look for /overlay/*/led (Superadmin)</span>
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { k: "classic", name: "Classic (Red / Orange)", desc: "Original full-bleed LED we've shipped — bold accent colour per auction.", bg: "linear-gradient(135deg,#7f1d1d 0%,#431407 100%)", stripe: "#f97316" },
                { k: "royal_gold", name: "Royal Gold (Premium)", desc: "Navy & gold stadium look with container-scaled 16:9 canvas and gold trims.", bg: "linear-gradient(135deg,#0a1b32 0%,#041023 100%)", stripe: "#d9ad42" },
              ].map((opt) => (
                <label
                  key={opt.k}
                  className={`relative rounded-lg border-2 cursor-pointer p-3 flex items-start gap-3 transition ${form.led_theme === opt.k ? "border-amber-500 bg-amber-50/70" : "border-gray-200 hover:border-amber-300 bg-white"}`}
                  data-testid={`settings-led-theme-${opt.k}`}
                >
                  <input
                    type="radio"
                    name="led_theme"
                    value={opt.k}
                    checked={form.led_theme === opt.k}
                    onChange={() => set("led_theme", opt.k)}
                    className="mt-1 accent-amber-500"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div style={{ width: 46, height: 26, borderRadius: 4, background: opt.bg, borderTop: `3px solid ${opt.stripe}`, flexShrink: 0 }} />
                      <div className="font-bold text-sm">{opt.name}</div>
                    </div>
                    <div className="text-[11px] text-gray-500 mt-1">{opt.desc}</div>
                  </div>
                </label>
              ))}
            </div>
            <div className="text-[10px] text-gray-500 mt-2">Open LED windows pick up the new theme within ~15 seconds (no refresh needed). The <code>/overlay/:id/led/classic</code> and <code>/overlay/:id/led/royal</code> direct links always force a specific theme.</div>
          </div>
        )}
        <div>
          <label className="label-mono block mb-2">Venue</label>
          <input value={form.venue} onChange={(e) => set("venue", e.target.value)} className="w-full bg-white border border-gray-200 rounded px-3 py-2 focus:border-blue-500 outline-none" />
        </div>
        <div>
          <label className="label-mono block mb-2">Status</label>
          <select value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="settings-status" className="w-full bg-white border border-gray-200 rounded px-3 py-2 focus:border-blue-500 outline-none">
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="label-mono block mb-2">Start Date</label>
          <input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className="w-full bg-white border border-gray-200 rounded px-3 py-2 focus:border-blue-500 outline-none" />
        </div>
        <div>
          <label className="label-mono block mb-2">End Date</label>
          <input type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} className="w-full bg-white border border-gray-200 rounded px-3 py-2 focus:border-blue-500 outline-none" />
        </div>
      </div>

      <div className="border-t border-gray-200 pt-6">
        <div className="label-mono mb-3">GLOBAL VALUES · applied to all teams & players in this auction</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div><label className="label-mono block mb-2">Team Budget (pts)</label>
            <div className="flex items-center gap-2">
              <input type="number" min="10" data-testid="settings-team-budget" value={form.default_team_budget} onChange={(e) => set("default_team_budget", e.target.value)} className="flex-1 min-w-0 bg-white border border-gray-200 rounded px-3 py-2 focus:border-blue-500 outline-none" />
              <button
                type="button"
                onClick={async () => {
                  try {
                    await api.patch(`/auctions/${auction.id}`, { settings: { ...auction.settings, default_team_budget: Number(form.default_team_budget) || 1000 } });
                    const { data } = await api.post(`/auctions/${auction.id}/sync-team-budgets`);
                    toast.success(`Synced ${data.teams_updated} team(s) to ${data.budget} pts`);
                    reload?.();
                  } catch (e) {
                    toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Sync failed");
                  }
                }}
                className="text-[11px] font-bold uppercase tracking-widest px-3 py-2 rounded bg-emerald-600 hover:bg-emerald-500 text-white flex-shrink-0"
                data-testid="sync-team-budgets"
                title="Rewrite every team's wallet to this new budget. Only works before any player is sold."
              >
                Sync to teams
              </button>
            </div>
          </div>
          <div><label className="label-mono block mb-2">Base Price (pts)</label>
            <input type="number" min="1" data-testid="settings-base-price" value={form.default_base_price} onChange={(e) => set("default_base_price", e.target.value)} className="w-full bg-white border border-gray-200 rounded px-3 py-2 focus:border-blue-500 outline-none" />
          </div>
          <div><label className="label-mono block mb-2">Min Players</label>
            <input type="number" min="1" value={form.min_players_per_team} onChange={(e) => set("min_players_per_team", e.target.value)} className="w-full bg-white border border-gray-200 rounded px-3 py-2 focus:border-blue-500 outline-none" />
          </div>
          <div><label className="label-mono block mb-2">Max Players</label>
            <div className="flex items-center gap-2">
              <input type="number" min="1" value={form.max_players_per_team} onChange={(e) => set("max_players_per_team", e.target.value)} className="flex-1 min-w-0 bg-white border border-gray-200 rounded px-3 py-2 focus:border-blue-500 outline-none" />
              <button
                type="button"
                onClick={async () => {
                  try {
                    await api.patch(`/auctions/${auction.id}`, { settings: { ...auction.settings, max_players_per_team: Number(form.max_players_per_team) || 15, min_players_per_team: Number(form.min_players_per_team) || 11 } });
                    const { data } = await api.post(`/auctions/${auction.id}/sync-team-slots`);
                    toast.success(`Synced ${data.teams_updated} team(s) → Min ${data.min_players} / Max ${data.max_players}`);
                    reload?.();
                  } catch (e) {
                    toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Sync failed");
                  }
                }}
                className="text-[11px] font-bold uppercase tracking-widest px-3 py-2 rounded bg-emerald-600 hover:bg-emerald-500 text-white flex-shrink-0"
                data-testid="sync-team-slots"
                title="Rewrite every team's min/max player slots to these values. Only works before any player is sold."
              >
                Sync to teams
              </button>
            </div>
          </div>
          <div><label className="label-mono block mb-2">Bid Timer (sec)</label>
            <input type="number" min="5" data-testid="settings-timer" value={form.timer_seconds} onChange={(e) => set("timer_seconds", e.target.value)} className="w-full bg-white border border-gray-200 rounded px-3 py-2 focus:border-blue-500 outline-none" />
          </div>
          {isSuperadmin && (
            <div className="col-span-2 md:col-span-3 border border-orange-300 bg-orange-50/50 rounded p-3">
              <label className="label-mono block mb-2 text-orange-700">Team Slot Count · Superadmin</label>
              <div className="flex items-center gap-3 flex-wrap">
                <input
                  type="number"
                  min={Math.max(2, auction.team_count || 2)}
                  max="60"
                  value={form.number_of_teams}
                  onChange={(e) => set("number_of_teams", e.target.value)}
                  className="w-32 bg-white border border-gray-200 rounded px-3 py-2 focus:border-orange-500 outline-none"
                  data-testid="settings-number-of-teams"
                />
                <span className="text-xs text-gray-700">
                  Currently <strong>{auction.team_count || 0}</strong> team(s) exist · plan tier {auction.number_of_teams}.
                  Increase this to add more teams; cannot go below existing count.
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6">
          <BidTiersEditor value={form.bid_increments} onChange={(v) => set("bid_increments", v)} />
        </div>

        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="label-mono mb-3">CUSTOM SOLD / UNSOLD ANNOUNCEMENT (PNG · GIF · 3GP · MP4)</div>
          <p className="text-xs text-gray-500 mb-4">
            Upload a custom sold / unsold PNG, animated GIF or short video (.3gp / .mp4 / .webm)
            that will flash on every overlay (LED, Stream, Auctioneer) when a player is sold or
            marked unsold. Animated media will loop for the duration below then hide.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ImageInput
              label="SOLD announcement image / GIF / video"
              value={form.sold_media_url}
              onChange={(v) => set("sold_media_url", v)}
              testId="settings-sold-media"
              maxKB={10240}
              acceptGif
              aspect="aspect-video"
            />
            <ImageInput
              label="UNSOLD announcement image / GIF / video"
              value={form.unsold_media_url}
              onChange={(v) => set("unsold_media_url", v)}
              testId="settings-unsold-media"
              maxKB={10240}
              acceptGif
              aspect="aspect-video"
            />
          </div>
          <div className="mt-3">
            <label className="label-mono block mb-2">Announcement duration (ms)</label>
            <input
              type="number"
              min="1000"
              max="10000"
              step="500"
              value={form.announce_duration_ms}
              onChange={(e) => set("announce_duration_ms", e.target.value)}
              data-testid="settings-announce-duration"
              className="w-40 bg-white border border-gray-200 rounded px-3 py-2 focus:border-blue-500 outline-none"
            />
            <span className="text-xs text-gray-500 ml-2">How long the announcement stays on screen (default 4000ms)</span>
          </div>
        </div>

        <div className="text-xs text-gray-500 mt-3">
          Note: existing teams keep their current budget. Only newly created teams/players will use the new defaults.
        </div>
      </div>

      {/* Iter24: Per-category base price + increment tiers + Icon toggle */}
      <CategoryConfigEditor
        value={form.category_config}
        onChange={(v) => set("category_config", v)}
        globalBase={form.default_base_price}
        globalTiers={form.bid_increments}
        auctionId={auction.id}
      />

      {isSuperadmin && (
        <div className="border border-emerald-300 bg-emerald-50/50 rounded p-4 space-y-3" data-testid="settings-payment-section">
          <div className="label-mono text-emerald-700">Registration Payment · Superadmin</div>
          <p className="text-[11px] text-gray-600">Upload the payment QR the players will scan. Set the fee amount &gt; 0 to require a UTR + payment screenshot on the public /register form. Gemini vision then auto-reads the UTR, verifies the amount matches this fee, and (when set below) checks the payee name — mismatches are rejected before submit. Duplicate screenshots and UTRs are rejected server-side.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label-mono block mb-2">Fee Amount (₹)</label>
              <input
                type="number"
                min="0"
                value={form.registration_fee}
                onChange={(e) => set("registration_fee", e.target.value)}
                className="w-full bg-white border border-gray-200 rounded px-3 py-2 focus:border-emerald-500 outline-none"
                data-testid="settings-registration-fee"
                placeholder="0 = free"
              />
            </div>
            <div>
              <label className="label-mono block mb-2">Payment QR image</label>
              <ImageInput
                value={form.payment_qr_url}
                onChange={(v) => set("payment_qr_url", v)}
                testId="settings-payment-qr"
                maxKB={2048}
                aspect="aspect-square"
              />
            </div>
            <div className="md:col-span-2">
              <label className="label-mono block mb-2">Expected Payee Name</label>
              <input
                type="text"
                value={form.expected_payee_name}
                onChange={(e) => set("expected_payee_name", e.target.value)}
                className="w-full bg-white border border-gray-200 rounded px-3 py-2 focus:border-emerald-500 outline-none"
                data-testid="settings-expected-payee-name"
                placeholder="e.g. NITISOFT TECHNOLOGIES (leave blank to skip payee-name check)"
              />
              <div className="text-[11px] text-gray-500 mt-1">The screenshot's "Paid to" name is fuzzy-matched against this. Case &amp; punctuation insensitive.</div>
            </div>
          </div>
        </div>
      )}

      {isSuperadmin && (
        <div className="border border-orange-200 rounded p-4 bg-orange-50/40">
          <label className="flex items-center gap-3 cursor-pointer" data-testid="settings-icon-auction-toggle">
            <input
              type="checkbox"
              checked={!!form.icon_auction_enabled}
              onChange={(e) => set("icon_auction_enabled", e.target.checked)}
              className="w-5 h-5 accent-orange-500"
            />
            <div>
              <div className="font-bold uppercase tracking-widest text-orange-700 text-xs">Icon Auction (Superadmin)</div>
              <div className="text-[11px] text-gray-600">When OFF, Icon-category players are excluded from Pick / Random on the Live Auction.</div>
            </div>
          </label>
        </div>
      )}

      <div className="flex justify-end">
        <button type="submit" disabled={busy} data-testid="save-settings-button" className="btn-primary">
          {busy ? "Saving…" : "Save Settings"}
        </button>
      </div>

      {isSuperadmin && (
        <RegistrationFormConfigEditor auction={auction} reload={reload} />
      )}
    </form>
  );
}
